import { Box, Badge, Circle, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { Check } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import TaskSlaCard from './TaskSlaCard'
import TaskPanelBadge from './TaskPanelBadge'
import {
  formatTaskRelativeTime,
  isTaskBlocked,
  isTaskComplete,
  isTaskSkipped,
} from './taskUtils'
import { badgesFromFormFields } from './modules/resolveFormFields'
import type { TaskFormFieldDefinition } from './modules/types'

export type PipelinePreviewStep = {
  id?: number | string
  title: string
  status_code?: string
  status_label?: string
  default_sla_minutes?: number | null
  sort_order?: number
  form_fields?: Array<{ module: string; label?: string }>
}

type Props = {
  tasks?: ChildTicketTask[]
  previewSteps?: PipelinePreviewStep[]
  onTaskClick?: (task: ChildTicketTask) => void
  compact?: boolean
}

function formatSlaMinutes(minutes?: number | null): string {
  if (minutes == null) return '—'
  if (minutes === 0) return '0m'
  if (minutes >= 1440) return `${Math.round(minutes / 1440)}d`
  if (minutes >= 60) return `${Math.round(minutes / 60)}h`
  return `${minutes}m`
}

export default function TaskPipelinePreview({ tasks = [], previewSteps, onTaskClick, compact }: Props) {
  const colors = useThemeColors()

  if (previewSteps && previewSteps.length > 0) {
    return (
      <VStack align="stretch" spacing={0}>
        {previewSteps.map((step, index) => (
          <Flex key={step.id ?? index} gap={3} align="flex-start" py={compact ? 2 : 3}>
            <Circle size="22px" bg="gray.100" color="gray.500" fontSize="xs" fontWeight="700">
              {index + 1}
            </Circle>
            <Box flex="1" minW={0}>
              <HStack spacing={2} flexWrap="wrap" mb={0.5}>
                <Text fontSize="sm" fontWeight="600" color={colors.text} noOfLines={1}>
                  {step.title}
                </Text>
                {badgesFromFormFields((step.form_fields ?? []) as TaskFormFieldDefinition[]).map((b) => (
                  <Badge key={b} fontSize="2xs" colorScheme="blue">
                    {b}
                  </Badge>
                ))}
              </HStack>
              <Text fontSize="xs" color={colors.textSecondary}>
                {step.status_label ?? step.status_code ?? '—'} · SLA {formatSlaMinutes(step.default_sla_minutes)}
              </Text>
            </Box>
          </Flex>
        ))}
      </VStack>
    )
  }

  const sequential = tasks.filter((t) => t.depends_on_task_id != null || t.workflow_template_step_id != null)
  const parallel = tasks.filter((t) => t.depends_on_task_id == null && t.workflow_template_step_id == null)
  const ordered = [...sequential].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
  )

  return (
    <VStack align="stretch" spacing={4}>
      {ordered.length > 0 ? (
        <VStack align="stretch" spacing={0}>
          {ordered.map((task, index) => {
            const skipped = isTaskSkipped(task)
            const done = isTaskComplete(task) && !skipped
            const blocked = isTaskBlocked(task, tasks)
            const active = !done && !skipped && !blocked

            return (
              <Flex key={task.id} gap={3} align="stretch">
                <VStack spacing={0} align="center" minW="28px">
                  <Circle
                    size="24px"
                    bg={skipped ? 'gray.400' : done ? 'green.500' : active ? 'blue.500' : 'gray.200'}
                    color={done || active || skipped ? 'white' : 'gray.500'}
                  >
                    {skipped ? (
                      <Text fontSize="xs" fontWeight="700">
                        —
                      </Text>
                    ) : done ? (
                      <Check size={14} />
                    ) : (
                      index + 1
                    )}
                  </Circle>
                  {index < ordered.length - 1 ? (
                    <Box w="2px" flex="1" minH="16px" bg={colors.borderLight} my={1} />
                  ) : null}
                </VStack>
                <Box flex="1" pb={4}>
                  <TaskSlaCard
                    slaDueAt={task.sla_due_at}
                    completedAt={task.actual_end}
                    onClick={onTaskClick ? () => onTaskClick(task) : undefined}
                    cursor={onTaskClick ? 'pointer' : undefined}
                  >
                    <HStack spacing={2} align="center" flexWrap="wrap">
                      <Text
                        fontSize="sm"
                        fontWeight="700"
                        color={skipped ? colors.textMuted : colors.text}
                        noOfLines={2}
                        textDecoration={skipped ? 'line-through' : undefined}
                      >
                        {task.title || task.notes || task.task_number}
                      </Text>
                      {skipped ? (
                        <Badge colorScheme="gray" fontSize="2xs">
                          Skipped
                        </Badge>
                      ) : null}
                      {task.task_number ? (
                        <Text fontSize="xs" color={colors.textMuted}>
                          · {task.task_number}
                        </Text>
                      ) : null}
                      <TaskPanelBadge task={task} />
                    </HStack>
                    <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>
                      {task.assignee_name ?? 'Unassigned'}
                      {' · '}
                      {task.status_label ?? task.status ?? '—'}
                      {' · '}
                      {formatTaskRelativeTime(task)}
                      {blocked ? ' · Waiting on previous step' : ''}
                    </Text>
                  </TaskSlaCard>
                </Box>
              </Flex>
            )
          })}
        </VStack>
      ) : null}

      {parallel.length > 0 ? (
        <Box>
          <Text fontSize="xs" fontWeight="700" color={colors.textMuted} textTransform="uppercase" mb={2}>
            Parallel work
          </Text>
          <VStack align="stretch" spacing={2}>
            {parallel.map((task) => (
              <TaskSlaCard
                key={task.id}
                slaDueAt={task.sla_due_at}
                completedAt={task.actual_end}
                onClick={onTaskClick ? () => onTaskClick(task) : undefined}
                cursor={onTaskClick ? 'pointer' : undefined}
              >
                <HStack spacing={2} align="center" flexWrap="wrap" mb={0.5}>
                  <Text fontSize="sm" fontWeight="600">{task.title || task.task_number}</Text>
                  <TaskPanelBadge task={task} />
                </HStack>
                <Text fontSize="xs" color={colors.textSecondary}>
                  {task.assignee_name ?? 'Unassigned'}
                  {' · '}
                  {task.status_label ?? task.status ?? '—'}
                  {' · '}
                  {formatTaskRelativeTime(task)}
                </Text>
              </TaskSlaCard>
            ))}
          </VStack>
        </Box>
      ) : null}
    </VStack>
  )
}
